import type { FilterGroup } from '@/types/SearchDefaults';
import type { FilterItem } from '@/types/FilterBar';
import { $t } from '@/composables/translations/useTranslate';

/**
 * Build FilterBar badge items from the current selection.
 *
 * Selection is keyed by composite value (`{hubProperty}.{hubValue}`). When a
 * composite appears under multiple groups in the curated config (e.g.
 * `location.beach` and `topics.beach` both map to
 * `AccommodationLocations.NearBeach`), we render exactly one chip per unique
 * composite — first match in config order wins. Duplicating the same label
 * in the chip row reads as a bug to users, and prefixing with the group name
 * makes chips too long. FilterList still checks every matching checkbox, so
 * the UI conveys the multi-group nature there instead.
 *
 * Clicking the chip toggles the composite key, which unchecks every option
 * that shares it. If no option matches (e.g. a legacy composite key seeded
 * by ibeSearchDefaults but not part of the curated config), we fall back to
 * the raw composite so the user can still remove it.
 */
export function resolveActiveFilterLabels(
  selectedCompositeKeys: string[],
  filterGroups: readonly FilterGroup[]
): FilterItem[] {
  return selectedCompositeKeys.map((compositeKey) => {
    for (const group of filterGroups) {
      const match = group.options.find(
        (opt) => `${opt.hubProperty}.${opt.hubValue}` === compositeKey
      );
      if (match) {
        return {
          id: compositeKey,
          label: $t(`ibe.filters.${group.id}.${match.id}.label`, match.id),
          category: group.id,
          value: compositeKey,
        };
      }
    }

    return {
      id: compositeKey,
      label: compositeKey,
      category: compositeKey.split('.')[0],
      value: compositeKey,
    };
  });
}
